import { useEffect, useRef, useState } from 'react';
import { EMOJIS_REACAO } from '@/conversas/emojisReacao';
import { escolherReacao } from '@/conversas/reacoes';
import { useConversas } from '@/conversas/ConversasProvider';
import { enviarReacao } from '@/services/conversasService';

/**
 * Reagir a uma mensagem, como no WhatsApp.
 *
 * Mora ao lado do botão de encaminhar, no mesmo canto da bolha, e aparece nas
 * mesmas condições (`:hover` e `:focus-within` no CSS) — um botão que só existe
 * no mouse fica inalcançável por Tab.
 *
 * O SELO em si não é desenhado aqui: quem o desenha é o `SelosDeReacao`, da
 * leitura de reações. Este componente só dispara a escrita; o selo aparece
 * porque a callable grava e a assinatura da thread devolve o documento.
 */

const MENSAGENS_ERRO = {
  'functions/permission-denied': 'Seu papel não atende o espaço desta conversa.',
  'functions/not-found': 'Esta mensagem não está mais disponível.',
  'functions/invalid-argument': 'Esse emoji não pode ser usado como reação.',
  'functions/internal': 'Não foi possível falar com o servidor. Tente de novo.',
};

/**
 * `unavailable` e `deadline-exceeded` ficam FORA do mapa de propósito: desde a
 * blindagem do envio, quem redige esses textos é o `canais.js`, que sabe se a
 * falha foi ao conectar ou ao esperar. Um texto fixo aqui apagaria o do servidor.
 */
const explicar = (e, generico) => MENSAGENS_ERRO[e?.code] ?? e?.message ?? generico;

export default function BotaoReagir({ mensagem }) {
  const { clinicaId, conversa } = useConversas();
  const [aberta, setAberta] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState(null);
  const caixa = useRef(null);

  // Clique fora e Esc fecham a paleta. Sem isso ela ficaria aberta sobre a
  // thread enquanto o atendente rola a conversa.
  useEffect(() => {
    if (!aberta) return undefined;
    const forade = (e) => {
      if (caixa.current && !caixa.current.contains(e.target)) setAberta(false);
    };
    const tecla = (e) => {
      if (e.key === 'Escape') setAberta(false);
    };
    document.addEventListener('mousedown', forade);
    document.addEventListener('keydown', tecla);
    return () => {
      document.removeEventListener('mousedown', forade);
      document.removeEventListener('keydown', tecla);
    };
  }, [aberta]);

  const minha = mensagem.reacoes?.clinica?.emoji ?? null;

  async function reagir(escolhido) {
    // A decisão de alternar/substituir/aplicar mora num módulo puro, testável
    // sem tela: é a regra que o atendente sente e a que mais erra na mão.
    const emoji = escolherReacao(minha, escolhido);

    setEnviando(true);
    setErro(null);
    try {
      await enviarReacao({
        clinicaId,
        conversaId: conversa.id,
        mensagemId: mensagem.id,
        emoji,
      });
      setAberta(false);
    } catch (e) {
      console.error('[OdontoSDR] Falha ao reagir:', e);
      setErro(explicar(e, 'Não foi possível reagir.'));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="msg-reagir-caixa" ref={caixa}>
      <button
        type="button"
        className="msg-reagir"
        onClick={() => {
          setErro(null);
          setAberta((v) => !v);
        }}
        title="Reagir a esta mensagem"
        aria-label="Reagir a esta mensagem"
        aria-expanded={aberta}
        disabled={enviando}
      >
        ☺
      </button>

      {aberta && (
        <div className="paleta-reacao" role="menu">
          {EMOJIS_REACAO.map((e) => (
            <button
              key={e}
              type="button"
              role="menuitem"
              // O já escolhido fica marcado: é o que avisa que clicar de novo
              // REMOVE, em vez de reagir uma segunda vez.
              className={minha === e ? 'paleta-emoji escolhido' : 'paleta-emoji'}
              onClick={() => reagir(e)}
              disabled={enviando}
              title={minha === e ? 'Remover esta reação' : `Reagir com ${e}`}
              aria-pressed={minha === e}
            >
              {e}
            </button>
          ))}
        </div>
      )}

      {erro && <span className="msg-reagir-erro">{erro}</span>}
    </div>
  );
}
